import { Loading } from "../../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  formatEuro,
  formatIban,
  invoiceDisplayStatus,
  periodLabel,
  SALES_INVOICE_STATUS_LABELS,
  type BillingRoundDto,
  type BillingRoundRow,
  type SalesAgreementRow,
  type SalesAgreementUpdateBody,
  type SalesInvoiceDetail,
  type SalesInvoiceRow,
  type SalesInvoiceStatus,
} from "@klantpunt/shared";
import { IconDownload } from "../../app/icons";
import { Meta } from "../../app/Meta";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { parseEuro } from "../../pages/Platform";

/**
 * Platformbeheer › Facturatie (docs/VERKOOP.md stap 4): maandronde, facturen en overeenkomsten.
 * Prijslijst en verkoper staan in SalesBilling.tsx.
 */

const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
const billingKey = ["platform", "sales", "billing"] as const;
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtDay = (iso: string | null) =>
  iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })
    : "";
const shiftPeriod = (p: string, n: number) => {
  const [y, m] = p.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const euroInput = (c: number | null) => (c === null ? "" : (c / 100).toFixed(2).replace(".", ","));

/** Verbruik van het PO-budget: al gefactureerd plus (licht) deze factuur. */
export function BudgetBar({ budget, used, add = 0 }: { budget: number; used: number; add?: number }) {
  const after = used + add;
  const pct = budget > 0 ? Math.round((after / budget) * 100) : 100;
  const tone = after > budget ? "bad" : pct >= 80 ? "warn" : "ok";
  const w1 = budget > 0 ? Math.min(100, (used / budget) * 100) : 100;
  const w2 = budget > 0 ? Math.max(0, Math.min(100, (after / budget) * 100) - w1) : 0;
  return (
    <span className="budgetbar">
      <span
        className="budgetbar-track"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={budget}
        aria-valuenow={Math.min(after, budget)}
        aria-label="PO-budget"
      >
        <i className="budgetbar-used" style={{ width: `${w1}%` }} />
        <i className={`budgetbar-add ${tone}`} style={{ width: `${w2}%` }} />
      </span>
      <span className="small muted num">
        {formatEuro(after)} van {formatEuro(budget)}
        {add > 0 && " na deze factuur"} · {pct}%
      </span>
    </span>
  );
}

function PoCell({ r }: { r: Pick<BillingRoundRow, "noPo" | "poNumber" | "reference"> }) {
  return r.noPo ? (
    <>
      <span className="chip">Zonder PO</span>
      {r.reference && <span className="small muted co-block">{r.reference}</span>}
    </>
  ) : (
    <b className="num">{r.poNumber}</b>
  );
}

const STATE_CHIP: Record<BillingRoundRow["state"], [string, string]> = {
  ready: ["Klaar", "tone tone-ok"],
  low: ["Klaar, let op", "tone tone-warn"],
  blocked: ["Tegengehouden", "tone tone-danger"],
  done: ["Gefactureerd", "tone tone-info"],
  later: ["Nog niet begonnen", ""],
  ended: ["Opgezegd", ""],
};

// ---------- Maandronde ----------

export function BillingRound() {
  const qc = useQueryClient();
  const toast = useToast();
  const [period, setPeriod] = useState(() => todayIso().slice(0, 7));
  const q = useQuery({
    queryKey: [...billingKey, "round", period],
    queryFn: () => api<BillingRoundDto>(`/platform/sales/billing/round?period=${period}`),
  });
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const [force, setForce] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    setSkip(new Set());
    setForce(new Set());
  }, [period]);

  const rows = q.data?.rows ?? [];
  const chosen = rows.filter(
    (r) =>
      ((r.state === "ready" || r.state === "low") && !skip.has(r.agreementId)) ||
      (r.state === "blocked" && force.has(r.agreementId)),
  );
  const ready = rows.filter((r) => r.state === "ready" || r.state === "low");
  const blocked = rows.filter((r) => r.state === "blocked");
  const run = useMutation({
    mutationFn: () =>
      api<{ created: string[] }>("/platform/sales/billing/round", {
        method: "POST",
        body: {
          period,
          agreementIds: chosen.map((r) => r.agreementId),
          force: chosen.filter((r) => r.state === "blocked").map((r) => r.agreementId),
        },
      }),
    onSuccess: async (r) => {
      setConfirm(false);
      await qc.invalidateQueries({ queryKey: billingKey });
      toast(r.created.length === 1 ? `Factuur ${r.created[0]} gemaakt` : `${r.created.length} facturen gemaakt`);
    },
    onError: (e) => toast(errMsg(e)),
  });
  const toggle = (set: Set<string>, id: string, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(id);
    else next.delete(id);
    return next;
  };

  return (
    <>
      <div className="billing-period">
        <button
          type="button"
          className="btn ghost small"
          onClick={() => setPeriod((p) => shiftPeriod(p, -1))}
          aria-label="Vorige maand"
        >
          ‹
        </button>
        <h2 className="billing-month">{periodLabel(period)}</h2>
        <button
          type="button"
          className="btn ghost small"
          onClick={() => setPeriod((p) => shiftPeriod(p, 1))}
          aria-label="Volgende maand"
        >
          ›
        </button>
        <span className="grow" />
        <button
          type="button"
          className="btn primary"
          disabled={!chosen.length || run.isPending}
          onClick={() => setConfirm(true)}
        >
          Facturen maken ({chosen.length})
        </button>
      </div>
      {q.isPending || q.isError ? (
        q.isError ? (
          <p className="muted">{errMsg(q.error)}</p>
        ) : (
          <Loading />
        )
      ) : (
        <>
          <div className="pf-kpis" role="group" aria-label="Totalen">
            <span className="pf-kpi">
              <span className="label">Klaar om te factureren</span>
              <strong className="num">{ready.length}</strong>
              <span className="small muted num">
                {formatEuro(ready.reduce((s, r) => s + r.amounts.subtotal, 0))} excl. btw
              </span>
            </span>
            <span className="pf-kpi">
              <span className="label">Tegengehouden</span>
              <strong className="num">{blocked.length}</strong>
              <span className="small muted">PO-budget op of PO verlopen</span>
            </span>
            <span className="pf-kpi">
              <span className="label">Openstaand</span>
              <strong className="num">{formatEuro(q.data.outstanding)}</strong>
              <span className="small muted num">
                {q.data.overdue ? `waarvan ${formatEuro(q.data.overdue)} te laat` : "niets te laat"}
              </span>
            </span>
            <span className="pf-kpi">
              <span className="label">Per maand</span>
              <strong className="num">{formatEuro(q.data.mrr)}</strong>
              <span className="small muted">lopende overeenkomsten, excl. btw</span>
            </span>
          </div>
          {rows
            .filter((r) => r.reason && (r.state === "blocked" || r.state === "low"))
            .map((r) => (
              <p key={r.agreementId} className={`alert${r.state === "blocked" ? "" : " info"}`}>
                <b>{r.customerName}:</b> {r.reason}
              </p>
            ))}
          <section className="panel tscroll">
            {rows.length ? (
              <table className="it sales-table billing-table" aria-label={`Maandronde ${periodLabel(period)}`}>
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="sr-only">Meenemen</span>
                    </th>
                    <th scope="col">Klant</th>
                    <th scope="col" className="dir-hide-narrow">
                      PO en budget
                    </th>
                    <th scope="col" className="num">
                      Excl. btw
                    </th>
                    <th scope="col" className="dir-hide-narrow">
                      Stand
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const open = r.state === "ready" || r.state === "low";
                    const checked = open ? !skip.has(r.agreementId) : force.has(r.agreementId);
                    const [label, tone] = STATE_CHIP[r.state];
                    return (
                      <tr key={r.agreementId}>
                        <td>
                          {(open || r.state === "blocked") && (
                            <input
                              type="checkbox"
                              checked={checked}
                              aria-label={
                                r.state === "blocked"
                                  ? `${r.customerName} toch factureren`
                                  : `${r.customerName} factureren`
                              }
                              onChange={(e) =>
                                open
                                  ? setSkip((s) => toggle(s, r.agreementId, !e.target.checked))
                                  : setForce((s) => toggle(s, r.agreementId, e.target.checked))
                              }
                            />
                          )}
                        </td>
                        <td className="tcell-title">
                          <Link to={`/platform/klanten/${r.customerId}`} className="co-rowtitle">
                            {r.customerName}
                          </Link>
                          <span className="small muted co-block">{r.summary}</span>
                          <span className="sales-stage-narrow">
                            <span className={`chip ${tone}`}>{label}</span>
                          </span>
                        </td>
                        <td className="dir-hide-narrow">
                          <PoCell r={r} />
                          {!r.noPo && r.poBudget !== null && (
                            <BudgetBar
                              budget={r.poBudget}
                              used={r.poUsed}
                              add={open || r.state === "blocked" ? r.amounts.subtotal : 0}
                            />
                          )}
                        </td>
                        <td className="num">{formatEuro(r.amounts.subtotal)}</td>
                        <td className="dir-hide-narrow">
                          <span className={`chip ${tone}`}>{label}</span>
                          {r.invoiceNumber && <span className="small muted co-block num">{r.invoiceNumber}</span>}
                          {r.state === "blocked" && checked && (
                            <span className="small muted co-block">Wordt bewust doorgezet</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="muted">
                Nog geen overeenkomsten. Een overeenkomst ontstaat als een klant online akkoord geeft op een offerte.
              </p>
            )}
          </section>
        </>
      )}
      {confirm && (
        <Modal
          title={`${chosen.length === 1 ? "1 factuur" : `${chosen.length} facturen`} maken`}
          onClose={() => setConfirm(false)}
          busy={run.isPending}
          footer={
            <>
              <span className="grow" />
              <button type="button" className="btn ghost" onClick={() => setConfirm(false)}>
                Annuleren
              </button>
              <button type="button" className="btn primary" disabled={run.isPending} onClick={() => run.mutate()}>
                {run.isPending ? "Bezig…" : "Facturen maken"}
              </button>
            </>
          }
        >
          <p>
            Voor {periodLabel(period)}. Elke factuur krijgt een nummer, de regels uit de overeenkomst en het PO-nummer
            of kenmerk. Een nummer wordt niet opnieuw gebruikt.
          </p>
          <table className="it" aria-label="Te maken facturen">
            <tbody>
              {chosen.map((r) => (
                <tr key={r.agreementId}>
                  <td>
                    {r.customerName}
                    <span className="small muted co-block">
                      {r.noPo ? `Zonder PO${r.reference ? `, kenmerk ${r.reference}` : ""}` : `PO ${r.poNumber}`}
                      {r.state === "blocked" && " · bewust doorgezet"}
                    </span>
                  </td>
                  <td className="num">{formatEuro(r.amounts.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small">
            Bedragen incl. btw. Versturen en incasso komen in de volgende stap; download de PDF bij Facturen.
          </p>
        </Modal>
      )}
    </>
  );
}

// ---------- Facturen ----------

const INVOICE_TONE: Record<SalesInvoiceStatus | "overdue", string> = {
  open: "tone tone-info",
  paid: "tone tone-ok",
  overdue: "tone tone-danger",
};

export function InvoiceList({ customerId }: { customerId?: string } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: [...billingKey, "invoices", customerId ?? "all"],
    queryFn: () => api<SalesInvoiceRow[]>(`/platform/sales/invoices${customerId ? `?customerId=${customerId}` : ""}`),
  });
  const [busy, setBusy] = useState<string | null>(null);
  const today = todayIso();
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: SalesInvoiceStatus }) =>
      api<SalesInvoiceDetail>(`/platform/sales/invoices/${v.id}`, { method: "PATCH", body: { status: v.status } }),
    onSuccess: async (inv) => {
      await qc.invalidateQueries({ queryKey: billingKey });
      toast(inv.status === "paid" ? `Factuur ${inv.number} betaald` : `Factuur ${inv.number} weer open`);
    },
    onError: (e) => toast(errMsg(e)),
  });
  const pdf = async (id: string) => {
    setBusy(id);
    try {
      const inv = await api<SalesInvoiceDetail>(`/platform/sales/invoices/${id}`);
      const { downloadInvoicePdf } = await import("./invoicePdf");
      await downloadInvoicePdf(inv);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  if (q.isPending || q.isError) return q.isError ? <p className="muted">{errMsg(q.error)}</p> : <Loading />;
  if (!q.data.length)
    return (
      <section className="panel">
        <p className="muted">Nog geen facturen. Facturen maak je in de maandronde.</p>
      </section>
    );
  return (
    <section className="panel tscroll">
      <table className="it sales-table" aria-label="Facturen">
        <thead>
          <tr>
            <th scope="col">Factuur</th>
            <th scope="col" className="dir-hide-narrow">
              PO of kenmerk
            </th>
            <th scope="col" className="num">
              Incl. btw
            </th>
            <th scope="col" className="dir-hide-narrow">
              Stand
            </th>
            <th scope="col">
              <span className="sr-only">Acties</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {q.data.map((i) => {
            const shown = invoiceDisplayStatus(i.status, i.dueOn, today);
            const chip = <span className={`chip ${INVOICE_TONE[shown]}`}>{SALES_INVOICE_STATUS_LABELS[shown]}</span>;
            return (
              <tr key={i.id}>
                <td className="tcell-title">
                  <b className="num">{i.number}</b> {!customerId && <span>{i.customerName}</span>}
                  <Meta
                    className="small muted co-block"
                    items={[periodLabel(i.period), i.forced && "bewust doorgezet"]}
                  />
                  <span className="sales-stage-narrow">{chip}</span>
                </td>
                <td className="dir-hide-narrow num">{i.poNumber || i.reference || "–"}</td>
                <td className="num">{formatEuro(i.total)}</td>
                <td className="dir-hide-narrow">
                  {chip}
                  <span className="small muted co-block">
                    {i.status === "paid" ? `Betaald ${fmtDay(i.paidOn)}` : `Vervalt ${fmtDay(i.dueOn)}`}
                  </span>
                </td>
                <td>
                  <span className="billing-actions">
                    <button
                      type="button"
                      className="btn small"
                      disabled={busy === i.id}
                      onClick={() => void pdf(i.id)}
                      aria-label={`PDF van factuur ${i.number}`}
                    >
                      <IconDownload size={16} aria-hidden="true" />
                      PDF
                    </button>
                    <button
                      type="button"
                      className="btn ghost small"
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate({ id: i.id, status: i.status === "paid" ? "open" : "paid" })}
                      aria-label={i.status === "paid" ? `Factuur ${i.number} weer open` : `Factuur ${i.number} betaald`}
                    >
                      {i.status === "paid" ? "Weer open" : "Betaald"}
                    </button>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// ---------- Overeenkomsten ----------

export function AgreementList({ customerId }: { customerId?: string } = {}) {
  const q = useQuery({
    queryKey: [...billingKey, "agreements", customerId ?? "all"],
    queryFn: () =>
      api<SalesAgreementRow[]>(`/platform/sales/agreements${customerId ? `?customerId=${customerId}` : ""}`),
  });
  const [edit, setEdit] = useState<SalesAgreementRow | null>(null);
  if (q.isPending || q.isError) return q.isError ? <p className="muted">{errMsg(q.error)}</p> : <Loading />;
  if (!q.data.length)
    return (
      <section className="panel">
        <p className="muted">
          Nog geen overeenkomsten. Een overeenkomst ontstaat als een klant online akkoord geeft op een offerte.
        </p>
      </section>
    );
  return (
    <>
      <div className="co-col">
        {q.data.map((a) => (
          <section key={a.id} className="panel co-panel" aria-label={`Overeenkomst ${a.customerName}`}>
            <div className="co-head">
              <div>
                <h2 className="billing-agreement-title">{a.customerName}</h2>
                <Meta
                  className="small muted"
                  items={[
                    <Link key="q" to={`/platform/offertes/${a.quoteId}`}>
                      {a.quoteNumber}
                    </Link>,
                    `getekend ${fmtDay(a.acceptedAt.slice(0, 10))} door ${a.signerName}`,
                  ]}
                />
              </div>
              <button type="button" className="btn small" onClick={() => setEdit(a)}>
                PO of gegevens wijzigen
              </button>
            </div>
            <dl className="kv">
              <dt>{a.noPo ? "Kenmerk" : "PO-nummer"}</dt>
              <dd className="num">
                {a.noPo ? a.reference || "Zonder PO" : a.poNumber}
                {a.poUntil && <span className="muted"> · geldig tot {fmtDay(a.poUntil)}</span>}
              </dd>
              {!a.noPo && (
                <>
                  <dt>PO-budget</dt>
                  <dd>
                    {a.poBudget !== null ? (
                      <BudgetBar budget={a.poBudget} used={a.poUsed} />
                    ) : (
                      <span className="muted">Geen budget; we bewaken niets</span>
                    )}
                  </dd>
                </>
              )}
              <dt>Looptijd</dt>
              <dd>
                {a.termMonths} maanden vanaf {fmtDay(a.startOn) || "akkoord"}
                {a.endsOn && <span className="warn"> · opgezegd per {fmtDay(a.endsOn)}</span>}
              </dd>
              <dt>Per maand</dt>
              <dd className="num">
                {formatEuro(a.monthly)} excl. btw
                {a.discountPct > 0 && (
                  <span className="muted">
                    {" "}
                    · {a.discountPct}% korting{a.discountMonths ? ` eerste ${a.discountMonths} maanden` : ""}
                  </span>
                )}
              </dd>
              <dt>Rekening</dt>
              <dd className="num">{formatIban(a.iban)}</dd>
              <dt>Facturen naar</dt>
              <dd>{a.invoiceEmail}</dd>
              <dt>Facturen</dt>
              <dd className="num">{a.invoiceCount}</dd>
            </dl>
          </section>
        ))}
      </div>
      {edit && <AgreementDialog a={edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function AgreementDialog({ a, onClose }: { a: SalesAgreementRow; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({
    noPo: a.noPo,
    poNumber: a.poNumber,
    reference: a.reference,
    poBudget: euroInput(a.poBudget),
    poUntil: a.poUntil ?? "",
    invoiceEmail: a.invoiceEmail,
    billingAddress: a.billingAddress,
    endsOn: a.endsOn ?? "",
  });
  const set = (p: Partial<typeof f>) => setF((v) => ({ ...v, ...p }));
  const budget = f.poBudget.trim() ? parseEuro(f.poBudget) : null;
  const budgetBad = f.poBudget.trim() !== "" && budget === null;
  const poBad = !f.noPo && f.poNumber.trim().length < 2;
  const body = useMemo<SalesAgreementUpdateBody>(
    () => ({
      noPo: f.noPo,
      poNumber: f.poNumber.trim(),
      reference: f.reference.trim(),
      poBudget: budget,
      poUntil: f.poUntil || null,
      invoiceEmail: f.invoiceEmail.trim(),
      billingAddress: f.billingAddress.trim(),
      endsOn: f.endsOn || null,
    }),
    [f, budget],
  );
  const save = useMutation({
    mutationFn: () => api(`/platform/sales/agreements/${a.id}`, { method: "PATCH", body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: billingKey });
      toast("Overeenkomst bijgewerkt");
      onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <Modal
      title={`Overeenkomst ${a.customerName}`}
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={poBad || budgetBad || save.isPending}
            onClick={() => save.mutate()}
          >
            Opslaan
          </button>
        </>
      }
    >
      <label className="check offer-check">
        <input type="checkbox" checked={f.noPo} onChange={(e) => set({ noPo: e.target.checked })} />
        <span>Zonder PO-nummer</span>
      </label>
      {f.noPo ? (
        <label className="field">
          <span>Eigen kenmerk op de factuur</span>
          <input value={f.reference} onChange={(e) => set({ reference: e.target.value })} />
        </label>
      ) : (
        <>
          <label className="field">
            <span>PO-nummer</span>
            <input
              value={f.poNumber}
              onChange={(e) => set({ poNumber: e.target.value })}
              aria-invalid={poBad || undefined}
            />
            {poBad && <span className="hint err">Vul het PO-nummer in.</span>}
          </label>
          <div className="grid2">
            <label className="field">
              <span>Budget op dit PO (€)</span>
              <input
                className="num"
                inputMode="decimal"
                value={f.poBudget}
                onChange={(e) => set({ poBudget: e.target.value })}
                aria-invalid={budgetBad || undefined}
              />
              <span className={budgetBad ? "hint err" : "hint"}>
                {budgetBad ? "Controleer het bedrag." : "Excl. btw. Leeg = geen bewaking."}
              </span>
            </label>
            <label className="field">
              <span>PO geldig tot</span>
              <input type="date" value={f.poUntil} onChange={(e) => set({ poUntil: e.target.value })} />
            </label>
          </div>
          <p className="muted small">
            Een nieuw PO-nummer begint met een leeg budget; wat op het oude PO is gefactureerd, telt niet mee.
          </p>
        </>
      )}
      <label className="field">
        <span>Facturen naar</span>
        <input type="email" value={f.invoiceEmail} onChange={(e) => set({ invoiceEmail: e.target.value })} />
      </label>
      <label className="field">
        <span>Factuuradres</span>
        <textarea rows={3} value={f.billingAddress} onChange={(e) => set({ billingAddress: e.target.value })} />
      </label>
      <label className="field">
        <span>Opgezegd per</span>
        <input type="date" value={f.endsOn} onChange={(e) => set({ endsOn: e.target.value })} />
        <span className="hint">Laatste dag waarover gefactureerd wordt. Leeg = loopt door.</span>
      </label>
    </Modal>
  );
}
